/**
 * Collection of helper functions for the application
 */

/**
 * Generates a unique ID with an optional prefix
 */
export const generateId = (prefix = '') => 
  `${prefix}${prefix ? '_' : ''}${Math.random().toString(36).slice(2, 11)}`;

/**
 * Formats a timestamp to a readable date string
 */
export const formatTimestamp = (date: Date) => 
  date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

/**
 * Formats a date with full date and time information
 */
export const formatFullDate = (date: Date) => 
  date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });

/**
 * Saves data to localStorage with a specific key
 */
export const saveToLocalStorage = <T>(key: string, data: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Loads data from localStorage by key with an optional default value
 */
export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Per-UID cache key helpers to prevent cross-account leakage
 */
export const withUid = (uid: string | null | undefined, key: string): string => {
  const safeUid = typeof uid === 'string' && uid.length > 0 ? uid : 'anon';
  return `appCache:${safeUid}:${key}`;
};

export const saveForUid = <T>(uid: string | null | undefined, key: string, data: T): void => {
  saveToLocalStorage(withUid(uid, key), data);
};

export const loadForUid = <T>(uid: string | null | undefined, key: string, defaultValue: T): T => {
  return loadFromLocalStorage(withUid(uid, key), defaultValue);
};

export const removeForUid = (uid: string | null | undefined, key: string): void => {
  try { localStorage.removeItem(withUid(uid, key)); } catch {}
};

/**
 * Checks if an item is empty (all properties are empty strings)
 */
export const isItemEmpty = (item: any): boolean => 
  Object.keys(item).filter(k => k !== 'id').every(key => !item[key]);

/**
 * Creates a debounced function that delays invoking the provided function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T, 
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}; 